import { OrganizationServiceApiClient } from '@highereducation/organizations';

const clientCache = new Map<string, OrganizationServiceApiClient>();

// edudirect schoolId -> organization UUID. The mapping is effectively
// immutable, so resolutions are memoized for the session to avoid re-querying
// the Organization Service on every step mount.
const schoolIdToOrgId = new Map<number, string>();

function getClient(baseUrl: string): OrganizationServiceApiClient {
  let client = clientCache.get(baseUrl);
  if (!client) {
    client = new OrganizationServiceApiClient({ baseUrl });
    clientCache.set(baseUrl, client);
  }
  return client;
}

/**
 * Resolve legacy EduDirect school IDs to organization UUIDs via the
 * Organization Service. Offerings keys on org UUIDs only, so the experience
 * layer resolves legacy ids here before querying offerings. Results are
 * memoized for the session; unknown ids contribute nothing. Returns UUIDs in
 * the order of the (deduped) input ids.
 */
export const resolveOrganizationIds = async (
  edudirectIds: number[],
  baseUrl: string,
): Promise<string[]> => {
  const unique = Array.from(new Set(edudirectIds)).filter((id) =>
    Number.isFinite(id),
  );
  if (!unique.length || !baseUrl) return [];

  const missing = unique.filter((id) => !schoolIdToOrgId.has(id));
  if (missing.length) {
    try {
      const { data } = await getClient(baseUrl).resolveExternalIds({
        edudirectIds: missing,
      });
      for (const pair of data) {
        schoolIdToOrgId.set(pair.edudirectId, pair.organizationId);
      }
    } catch (error) {
      console.error('Failed to resolve organization ids:', error);
      return [];
    }
  }

  const orgIds: string[] = [];
  for (const id of unique) {
    const orgId = schoolIdToOrgId.get(id);
    if (orgId && !orgIds.includes(orgId)) orgIds.push(orgId);
  }
  return orgIds;
};

/**
 * Test-only: clears the session resolution cache.
 */
export const __clearOrganizationIdCache = (): void => {
  schoolIdToOrgId.clear();
  clientCache.clear();
};
